import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import GenderIcon from "./GenderIcon";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import {
    FaCheck,
    FaChevronDown,
    FaChevronUp,
    FaEnvelope,
    FaHome,
    FaLock,
    FaPhone,
    FaTimes,
    FaTrash,
    FaUndo,
    FaUser,
    FaUserTie,
} from "react-icons/fa";
import { useCan } from "../authz/useCan";
import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import {
    getChildAge,
    getClassroomPlacementCandidates,
    filterClassroomsForCurrentSchoolYear,
    getOnboardingProgress,
    getSchoolYearLabel,
    toAmsterdamDateTimeInputValue,
    APPLICATION_STEP_KEYS,
    getApplicationStepCompletion,
    getUndoBlockReason,
} from "./familyApplicationOnboarding";

const FamilyApplicationTable = ({
    applications,
    drawerCloseSignal = 0,
    onApprove,
    onReject,
    onDelete,
    onRestore,
    onAssignTeacher,
    onWorkflowStep,
    teachers = [],
    teachersLoading = false,
    teacherLookup = new Map(),
    currentTeacherId = null,
    emptyMessage,
    showStatus = false,
}) => {
    const { t } = useTranslation();
    const approvalEnabled = Boolean(onApprove);
    const canViewBsn = useCan("children.bsn", "view");
    const [visibleBsns, setVisibleBsns] = useState(() => new Set());
    const [revealedBsns, setRevealedBsns] = useState({});
    const [bsnLoadingByChild, setBsnLoadingByChild] = useState({});
    const [bsnErrorByChild, setBsnErrorByChild] = useState({});
    const [drawerApplicationKey, setDrawerApplicationKey] = useState(null);
    const [onboardingExpanded, setOnboardingExpanded] = useState(true);
    const [editingOnboardingStep, setEditingOnboardingStep] = useState(null);
    const [classrooms, setClassrooms] = useState([]);
    const [classroomsLoading, setClassroomsLoading] = useState(false);
    const [childPlacements, setChildPlacements] = useState({});
    const [placementDialog, setPlacementDialog] = useState(null);
    const drawerCloseRef = useRef(null);
    const previousDrawerCloseSignalRef = useRef(drawerCloseSignal);

    const maskBsn = (value) => {
        if (!value) return "—";
        const str = String(value).trim();
        if (str === "**") return "•".repeat(8);
        if (/^\*+$/.test(str)) return "•".repeat(Math.max(8, str.length));
        const digitsOnly = str.replace(/\D/g, "");
        if (!digitsOnly.length) return str;
        return "•".repeat(Math.max(8, digitsOnly.length));
    };

    const applicationChildBsnKey = (applicationId, childIndex) => `${applicationId ?? "app"}:${childIndex}`;
    const applicationKey = (app, index) => String(app?.id ?? index);

    const personName = (person) =>
        [person?.firstName, person?.lastName].filter(Boolean).join(" ") ||
        [person?.first_name, person?.last_name].filter(Boolean).join(" ") ||
        person?.displayName ||
        person?.name ||
        "—";

    const familyName = (app, primaryParent, secondaryParent) =>
        app?.familyName ||
        app?.family_name ||
        primaryParent?.lastName ||
        primaryParent?.last_name ||
        secondaryParent?.lastName ||
        secondaryParent?.last_name ||
        personName(primaryParent) ||
        personName(secondaryParent);

    const childCountLabel = (count) =>
        count === 1
            ? t("applications.children_count_one", { defaultValue: "1 kind" })
            : t("applications.children_count_other", {
                  count,
                  defaultValue: `${count} kinderen`,
              });

    const formatAddress = (person) =>
        [person?.address, [person?.zipCode, person?.city].filter(Boolean).join(" ")]
            .filter(Boolean)
            .join(", ") || "—";

    const openDrawer = (app, index) => {
        const progress = getOnboardingProgress(app);
        setDrawerApplicationKey(applicationKey(app, index));
        setEditingOnboardingStep(null);
        setOnboardingExpanded(!progress.isComplete);
    };

    const closeDrawer = useCallback(() => {
        setDrawerApplicationKey(null);
        setEditingOnboardingStep(null);
    }, []);

    useEffect(() => {
        if (previousDrawerCloseSignalRef.current === drawerCloseSignal) return;
        previousDrawerCloseSignalRef.current = drawerCloseSignal;
        closeDrawer();
    }, [drawerCloseSignal, closeDrawer]);

    useEffect(() => {
        if (!approvalEnabled) return;
        let active = true;
        setClassroomsLoading(true);
        http.get("/classrooms")
            .then((classroomsResponse) => {
                if (!active) return;
                const classroomData = classroomsResponse.data;
                const allClassrooms = Array.isArray(classroomData)
                    ? classroomData : (classroomData?.content ?? classroomData?.items ?? classroomData?.data ?? []);
                const schoolYears = allClassrooms.map((classroom) => classroom?.schoolYear).filter(Boolean);
                setClassrooms(filterClassroomsForCurrentSchoolYear(allClassrooms, schoolYears));
            })
            .catch(() => { if (active) setClassrooms([]); })
            .finally(() => { if (active) setClassroomsLoading(false); });
        return () => { active = false; };
    }, [approvalEnabled]);

    const classroomLabel = (classroom) => {
        const name = classroom?.className || classroom?.name || t("applications.placement.unknown_classroom", { defaultValue: "Unknown classroom" });
        const year = getSchoolYearLabel(classroom);
        return year ? `${name} · ${year}` : name;
    };

    const setPlacement = (applicationId, childIndex, classroomId) => {
        const classroom = classrooms.find((item) => String(item.id) === String(classroomId));
        setChildPlacements((current) => ({
            ...current,
            [applicationId]: {
                ...(current[applicationId] || {}),
                [childIndex]: classroomId ? { classroomId, grade: classroom?.grade ?? null, setCurrent: true } : undefined,
            },
        }));
    };

    const fetchApplicationChildBsn = async (applicationId, childIndex, stateKey) => {
        const key = stateKey || applicationChildBsnKey(applicationId, childIndex);

        if (!applicationId && applicationId !== 0) {
            setBsnErrorByChild((prev) => ({
                ...prev,
                [key]: t("applications.child_bsn_reveal_unavailable", {
                    defaultValue: "The BSN cannot be retrieved for this child.",
                }),
            }));
            return null;
        }

        setBsnErrorByChild((prev) => ({ ...prev, [key]: null }));
        setBsnLoadingByChild((prev) => ({ ...prev, [key]: true }));
        try {
            const res = await http.get(
                `/applications/${encodeURIComponent(String(applicationId))}/children/${encodeURIComponent(String(childIndex))}/bsn`
            );
            const data = res?.data;
            const value = typeof data === "string" ? data : data?.bsn;
            setRevealedBsns((prev) => ({ ...prev, [key]: value ?? null }));
            return value ?? null;
        } catch (err) {
            setBsnErrorByChild((prev) => ({ ...prev, [key]: mapHttpErrorToMessage(err, t) }));
            return null;
        } finally {
            setBsnLoadingByChild((prev) => ({ ...prev, [key]: false }));
        }
    };

    useEffect(() => {
        setVisibleBsns(new Set());
        setRevealedBsns({});
        setBsnLoadingByChild({});
        setBsnErrorByChild({});
    }, [applications]);

    useEffect(() => {
        if (!canViewBsn) {
            setVisibleBsns(new Set());
            setRevealedBsns({});
            setBsnLoadingByChild({});
            setBsnErrorByChild({});
        }
    }, [canViewBsn]);

    useEffect(() => {
        if (!drawerApplicationKey) return;
        const selectedApplication = (applications || []).find((app, index) =>
            applicationKey(app, index) === drawerApplicationKey
        );
        if (getOnboardingProgress(selectedApplication).isComplete) {
            setOnboardingExpanded(false);
            setEditingOnboardingStep(null);
        }
    }, [applications, drawerApplicationKey]);

    useEffect(() => {
        if (!drawerApplicationKey) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") closeDrawer();
        };
        window.addEventListener("keydown", onKeyDown);
        window.setTimeout(() => drawerCloseRef.current?.focus?.(), 0);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [drawerApplicationKey, closeDrawer]);

    if (!applications || applications.length === 0) {
        return (
            <p className="italic text-gray-600 dark:text-gray-300">
                {emptyMessage || t("applications.no_pending", "Geen openstaande aanvragen")}
            </p>
        );
    }

    const toDate = (value) => {
        if (!value) return null;
        if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
        if (Array.isArray(value)) {
            const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0, nano = 0] = value;
            const millis = Math.floor(nano / 1_000_000);
            const d = new Date(year, month - 1, day, hour, minute, second, millis);
            return Number.isNaN(d.getTime()) ? null : d;
        }
        if (typeof value === "string" || typeof value === "number") {
            const d = new Date(value);
            return Number.isNaN(d.getTime()) ? null : d;
        }
        return null;
    };

    const formatDate = (value) => {
        const parsed = toDate(value);
        return parsed ? format(parsed, "P", { locale: nl }) : "-";
    };

    const formatDateTime = (value) => {
        const parsed = toDate(value);
        if (!parsed) return "-";
        if (typeof value === "string" && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
            return new Intl.DateTimeFormat("nl-NL", {
                timeZone: "Europe/Amsterdam",
                dateStyle: "short",
                timeStyle: "short",
            }).format(parsed);
        }
        return format(parsed, "Pp", { locale: nl });
    };

    // Same five steps, labels and numbering in the list and in the detail drawer.
    const buildApplicationSteps = (application) => {
        const completion = getApplicationStepCompletion(application);
        const labels = {
            HANDLER: t("applications.workflow.handler_short", { defaultValue: "Teacher" }),
            BIOGRAPHY_MEETING_SCHEDULED: t("applications.workflow.scheduled_short", { defaultValue: "Plan meeting" }),
            BIOGRAPHY_PAYMENT: t("applications.workflow.payment_short", { defaultValue: "Payment" }),
            BIOGRAPHY_MEETING: t("applications.workflow.meeting_short", { defaultValue: "Biography meeting" }),
            APPROVED: t("applications.workflow.approved", { defaultValue: "Approved" }),
        };
        const details = {
            BIOGRAPHY_MEETING_SCHEDULED: application.biographyMeetingScheduledAt
                ? formatDateTime(application.biographyMeetingScheduledAt)
                : null,
        };
        return APPLICATION_STEP_KEYS.map((key) => ({
            key,
            label: labels[key],
            completed: completion[key],
            detail: details[key] || null,
        }));
    };

    const resolveTeacherLabel = (id) => {
        if (!id) return "";
        const normalized = String(id).trim();
        if (!normalized) return "";
        if (typeof teacherLookup.get === "function") {
            return teacherLookup.get(normalized) || teacherLookup.get(normalized.toLowerCase()) || normalized;
        }
        return teacherLookup?.[normalized] || teacherLookup?.[normalized.toLowerCase()] || normalized;
    };

    const statusClasses = {
        NEW: "border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-500 dark:bg-sky-900/30 dark:text-sky-100",
        PENDING:
            "border-yellow-300 bg-yellow-50 text-yellow-800 dark:border-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-100",
        APPROVED:
            "border-green-300 bg-green-50 text-green-800 dark:border-green-600 dark:bg-green-900/30 dark:text-green-100",
        REJECTED:
            "border-red-300 bg-red-50 text-red-800 dark:border-red-600 dark:bg-red-900/30 dark:text-red-100",
    };

    const drawerEntry = (applications || [])
        .map((app, index) => ({ app, index, key: applicationKey(app, index) }))
        .find((entry) => entry.key === drawerApplicationKey);
    const drawerApp = drawerEntry?.app || null;

    return (
        <div className="space-y-4 text-gray-900 dark:text-gray-100">
            <style>{`
                .family-application-card {
                    container-type: inline-size;
                }

                @container (max-width: 90rem) {
                    .family-application-progress {
                        display: none;
                    }

                    .family-application-progress-compact {
                        display: flex;
                    }
                }

                @keyframes onboarding-panel-enter {
                    from { opacity: 0; transform: translateX(0.5rem); }
                    to { opacity: 1; transform: translateX(0); }
                }

                .onboarding-panel-enter {
                    animation: onboarding-panel-enter 200ms ease-out;
                }
            `}</style>
            {applications.map((app, index) => {
                const parent1 = app.parent1 || {};
                const parent2 = app.parent2 || {};
                const overviewParent = personName(parent1) !== "—" ? parent1 : parent2;
                const children = app.children || [];
                const submittedAt =
                    app.createdAt ||
                    app.submittedAt ||
                    app.createdOn ||
                    app.created ||
                    null;
                const updatedAt = app.updatedAt || app.updated_at || app.modifiedAt || app.modified || submittedAt;
                const assignedTeacherLabel = resolveTeacherLabel(app.assignedTeacherId);
                const normalizedStatus = (app.status || "").toLowerCase();
                const statusLabel = t(`applications.status.${normalizedStatus}`, app.status);
                const teacherSummary = assignedTeacherLabel || t("applications.no_handler_assigned", "Geen behandelaar");
                const childCount = children.length;
                const resolvedFamilyName = familyName(app, overviewParent, parent2);
                const overviewSteps = buildApplicationSteps(app);
                const firstIncompleteStepIndex = overviewSteps.findIndex((step) => !step.completed);
                const currentWorkflowStepIndex = firstIncompleteStepIndex === -1
                    ? overviewSteps.length - 1
                    : firstIncompleteStepIndex;
                const currentWorkflowStep = overviewSteps[currentWorkflowStepIndex];

                return (
                    <div
                        key={index}
                        role="button"
                        tabIndex={0}
                        onClick={() => openDrawer(app, index)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                openDrawer(app, index);
                            }
                        }}
                        className="family-application-card lumen-plain-card cursor-pointer rounded-2xl p-4 shadow-md shadow-indigo-200/30 transition hover:shadow-xl hover:shadow-indigo-300/40 focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:shadow-indigo-950/30 dark:hover:shadow-indigo-800/40"
                    >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="min-w-0">
                                <h2 className="truncate text-lg font-bold text-indigo-700 dark:text-indigo-200">
                                    {t("applications.family_request_title", {
                                        familyName: resolvedFamilyName,
                                        defaultValue: `Aanvraag - Familie ${resolvedFamilyName}`,
                                    })}
                                </h2>
                                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                                    <span className="font-medium text-gray-800 dark:text-gray-100">{personName(overviewParent)}</span>
                                    <span aria-hidden="true">·</span>
                                    <span>{childCountLabel(childCount)}</span>
                                    <span aria-hidden="true">·</span>
                                    <span>
                                        {t("applications.assigned_handler_short", { defaultValue: "Teacher" })}:{" "}
                                        {teacherSummary}
                                    </span>
                                    {updatedAt && (
                                        <>
                                            <span aria-hidden="true">·</span>
                                            <span>
                                                {t("applications.updated_at", { defaultValue: "Bijgewerkt" })}:{" "}
                                                {formatDateTime(updatedAt)}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div
                                className="family-application-progress-compact hidden min-w-0 flex-1 items-center justify-center gap-3 px-2 text-indigo-700 dark:text-indigo-200"
                                aria-label={t("applications.workflow.progress", { defaultValue: "Application progress" })}
                            >
                                <ol className="flex shrink-0 items-center">
                                    {overviewSteps.map((step, stepIndex) => {
                                        const active = stepIndex === currentWorkflowStepIndex;
                                        return (
                                            <li key={step.key} className="flex items-center">
                                                <span
                                                    title={step.label}
                                                    aria-label={step.label}
                                                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-bold ${
                                                        step.completed
                                                            ? "border-indigo-600 bg-indigo-600 text-white dark:border-indigo-400 dark:bg-indigo-500"
                                                            : active
                                                              ? "border-indigo-400 bg-white text-indigo-600 ring-2 ring-indigo-100 dark:bg-slate-900 dark:text-indigo-300 dark:ring-indigo-900/60"
                                                              : "border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                                                    }`}
                                                >
                                                    {step.completed ? <FaCheck aria-hidden="true" /> : stepIndex + 1}
                                                </span>
                                                {stepIndex < overviewSteps.length - 1 && (
                                                    <span
                                                        aria-hidden="true"
                                                        className={`h-0.5 w-3 ${overviewSteps[stepIndex + 1].completed ? "bg-indigo-500" : "bg-slate-200 dark:bg-slate-700"}`}
                                                    />
                                                )}
                                            </li>
                                        );
                                    })}
                                </ol>
                                <span className="min-w-0 truncate text-sm font-semibold" title={currentWorkflowStep.label}>
                                    {currentWorkflowStep.label}
                                </span>
                            </div>

                            <ol
                                className="family-application-progress flex min-w-0 w-full flex-1 items-start justify-center lg:px-6"
                                aria-label={t("applications.workflow.progress", { defaultValue: "Application progress" })}
                            >
                                {overviewSteps.map((step, stepIndex) => {
                                    const isNext = !step.completed && overviewSteps.slice(0, stepIndex).every((item) => item.completed);
                                    return (
                                        <li key={step.key} className="flex min-w-0 flex-1 items-start last:flex-none">
                                            <div className="flex w-11 shrink-0 flex-col items-center sm:w-20">
                                                <span
                                                    title={step.label}
                                                    aria-label={step.label}
                                                    className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-[11px] font-bold transition sm:h-8 sm:w-8 ${
                                                        step.completed
                                                            ? "border-indigo-600 bg-indigo-600 text-white shadow-sm shadow-indigo-300 dark:border-indigo-400 dark:bg-indigo-500"
                                                            : isNext
                                                              ? "border-indigo-400 bg-white text-indigo-600 ring-4 ring-indigo-100 dark:bg-slate-900 dark:text-indigo-300 dark:ring-indigo-900/60"
                                                              : "border-slate-200 bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                                                    }`}
                                                >
                                                    {step.completed ? <FaCheck aria-hidden="true" /> : stepIndex + 1}
                                                </span>
                                                <span className={`mt-1 hidden max-w-full text-center text-[10px] font-semibold leading-tight sm:block sm:text-[11px] ${step.completed || isNext ? "text-indigo-700 dark:text-indigo-200" : "text-slate-400 dark:text-slate-500"}`}>
                                                    {step.label}
                                                </span>
                                                {step.detail && (
                                                    <span className="mt-0.5 whitespace-nowrap text-center text-[9px] font-medium text-slate-500 dark:text-slate-400">
                                                        {step.detail}
                                                    </span>
                                                )}
                                            </div>
                                            {stepIndex < overviewSteps.length - 1 && (
                                                <span
                                                    aria-hidden="true"
                                                    className={`mt-3.5 h-0.5 min-w-0 flex-1 sm:mt-4 sm:min-w-3 ${overviewSteps[stepIndex + 1].completed ? "bg-indigo-500" : "bg-slate-200 dark:bg-slate-700"}`}
                                                />
                                            )}
                                        </li>
                                    );
                                })}
                            </ol>

                            <div className="flex shrink-0 flex-wrap items-center gap-2">
                                <span
                                    className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${
                                        statusClasses[(app.status || "").toUpperCase()] ||
                                        "border-gray-300 bg-gray-50 text-gray-800 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-100"
                                    }`}
                                >
                                    {statusLabel}
                                </span>
                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        openDrawer(app, index);
                                    }}
                                    className="lumen-glow-button text-sm"
                                >
                                    {t("applications.open_request", { defaultValue: "Openen" })}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })}

            {drawerApp && typeof document !== "undefined" && createPortal((
                <div className="fixed inset-0 z-[9999]">
                    <button
                        type="button"
                        className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
                        onClick={closeDrawer}
                        aria-label={t("common.close", { defaultValue: "Close" })}
                    />
                    <aside
                        className="absolute right-0 top-0 h-full w-full max-w-2xl overflow-y-auto border-l border-indigo-100/60 bg-white/95 p-5 shadow-2xl dark:border-indigo-500/30 dark:bg-slate-950/95 sm:p-6"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="application-drawer-title"
                    >
                        {(() => {
                            const parent1 = drawerApp.parent1 || {};
                            const parent2 = drawerApp.parent2 || {};
                            const parents = [parent1, parent2].filter((p) => personName(p) !== "—");
                            const children = Array.isArray(drawerApp.children) ? drawerApp.children : [];
                            const assignedTeacherLabel = resolveTeacherLabel(drawerApp.assignedTeacherId);
                            const hasAssignedTeacher = Boolean(drawerApp.assignedTeacherId);
                            const normalizedStatus = (drawerApp.status || "").toLowerCase();
                            const statusLabel = t(`applications.status.${normalizedStatus}`, drawerApp.status);
                            const statusClass =
                                statusClasses[(drawerApp.status || "").toUpperCase()] ||
                                "border-gray-300 bg-gray-50 text-gray-800 dark:border-gray-600 dark:bg-gray-900/40 dark:text-gray-100";
                            const workflowSteps = [
                                {
                                    key: "HANDLER",
                                    label: hasAssignedTeacher
                                        ? t("applications.workflow.handler", { defaultValue: "Teacher" })
                                        : t("applications.workflow.choose_handler", { defaultValue: "Choose a teacher" }),
                                    shortLabel: t("applications.workflow.handler_short", { defaultValue: "Teacher" }),
                                    completed: hasAssignedTeacher,
                                    detail: assignedTeacherLabel,
                                },
                                {
                                    key: "BIOGRAPHY_MEETING_SCHEDULED",
                                    field: "biographyMeetingScheduledAt",
                                    label: t("applications.workflow.scheduled", { defaultValue: "Plan biography meeting" }),
                                    shortLabel: t("applications.workflow.scheduled_short", { defaultValue: "Plan meeting" }),
                                    help: t("applications.workflow.scheduled_help", { defaultValue: "Choose the date and time for the biography meeting." }),
                                    completed: Boolean(drawerApp.biographyMeetingScheduledAt),
                                    detail: drawerApp.biographyMeetingScheduledAt ? formatDateTime(drawerApp.biographyMeetingScheduledAt) : "",
                                    requiresDate: true,
                                },
                                {
                                    key: "BIOGRAPHY_PAYMENT",
                                    field: "biographyPaymentCompletedAt",
                                    label: t("applications.workflow.payment", { defaultValue: "Biography meeting payment" }),
                                    shortLabel: t("applications.workflow.payment_short", { defaultValue: "Payment" }),
                                    help: t("applications.workflow.payment_help", {
                                        defaultValue: "The parent pays for the biography meeting in advance. Record here that the payment has been received; after that the meeting can be completed.",
                                    }),
                                    confirmLabel: t("applications.workflow.payment_confirm", { defaultValue: "Payment received" }),
                                    completed: Boolean(drawerApp.biographyPaymentCompletedAt),
                                    detail: drawerApp.biographyPaymentCompletedAt
                                        ? t("applications.workflow.received_on", {
                                            date: formatDateTime(drawerApp.biographyPaymentCompletedAt),
                                            defaultValue: "Received on {{date}}",
                                        })
                                        : "",
                                },
                                {
                                    key: "BIOGRAPHY_MEETING",
                                    field: "biographyMeetingCompletedAt",
                                    label: t("applications.workflow.meeting", { defaultValue: "Biography meeting" }),
                                    shortLabel: t("applications.workflow.meeting_short", { defaultValue: "Biography meeting" }),
                                    help: t("applications.workflow.meeting_help", { defaultValue: "The biography meeting has been completed." }),
                                    confirmLabel: t("applications.workflow.meeting_confirm", { defaultValue: "Meeting completed" }),
                                    completed: Boolean(drawerApp.biographyMeetingCompletedAt),
                                    detail: drawerApp.biographyMeetingCompletedAt
                                        ? t("applications.workflow.completed_on", {
                                            date: formatDateTime(drawerApp.biographyMeetingCompletedAt),
                                            defaultValue: "Completed on {{date}}",
                                        })
                                        : "",
                                },
                            ];
                            // Step 5 of the shared order; approving happens through the Actions above.
                            const approvedStep = buildApplicationSteps(drawerApp).find((step) => step.key === "APPROVED");
                            const undoBlockMessages = {
                                meeting_completed: t("applications.workflow.undo_blocked_meeting_completed", {
                                    defaultValue: "Can no longer be undone: the biography meeting has already been completed.",
                                }),
                                approved: t("applications.workflow.undo_blocked_approved", {
                                    defaultValue: "Can no longer be undone: the application has already been approved.",
                                }),
                            };
                            const { activeStepIndex: activeWorkflowStepIndex, isComplete: onboardingComplete } =
                                getOnboardingProgress(drawerApp);
                            const selectedWorkflowStepIndex = Math.max(
                                0,
                                editingOnboardingStep
                                    ? workflowSteps.findIndex((step) => step.key === editingOnboardingStep)
                                    : activeWorkflowStepIndex === -1
                                      ? workflowSteps.length - 1
                                      : activeWorkflowStepIndex
                            );
                            const selectedWorkflowStep = workflowSteps[selectedWorkflowStepIndex];

                            const renderChildRow = (child, childIndex) => {
                                const key = applicationChildBsnKey(drawerApp.id, childIndex);
                                const hasBsn = Boolean(child?.bsn);
                                const shown = visibleBsns.has(key);
                                const loading = Boolean(bsnLoadingByChild[key]);
                                const error = bsnErrorByChild[key];
                                const revealedValue = revealedBsns[key];
                                const revealedText = String(revealedValue ?? "").trim();
                                const canToggleBsn = canViewBsn && hasBsn;
                                const displayBsn =
                                    shown && !error
                                        ? loading
                                            ? t("common.loading", { defaultValue: "Loading..." })
                                            : revealedText && revealedText !== "**"
                                              ? revealedText
                                              : "-"
                                        : hasBsn
                                          ? maskBsn(child.bsn)
                                          : "-";
                                return (
                                    <>
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center text-base text-blue-600 dark:text-blue-300">
                                            <GenderIcon gender={child.gender} />
                                        </span>
                                        <span className="min-w-[9rem] flex-1">
                                            <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {child.firstName} {child.lastName}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {formatDate(child.dateBirth)}
                                            </span>
                                        </span>
                                        <span className="hidden h-6 w-px shrink-0 bg-gray-200 dark:bg-gray-700 sm:block" />
                                        <span className="ml-0 flex min-w-0 flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-300 sm:ml-auto sm:justify-end sm:text-right">
                                            <span className="font-semibold">BSN:</span>
                                            <span className="font-semibold tracking-widest text-gray-700 dark:text-gray-200">
                                                {displayBsn}
                                            </span>
                                            {canToggleBsn && (
                                                <button
                                                    type="button"
                                                    onClick={async (event) => {
                                                        event.stopPropagation();
                                                        if (shown) {
                                                            setVisibleBsns((prev) => {
                                                                const next = new Set(prev);
                                                                next.delete(key);
                                                                return next;
                                                            });
                                                            return;
                                                        }
                                                        setVisibleBsns((prev) => {
                                                            const next = new Set(prev);
                                                            next.add(key);
                                                            return next;
                                                        });
                                                        if (!drawerApp.id && drawerApp.id !== 0) {
                                                            setBsnErrorByChild((prev) => ({
                                                                ...prev,
                                                                [key]: t("applications.child_bsn_reveal_unavailable", {
                                                                    defaultValue: "The BSN cannot be retrieved for this child.",
                                                                }),
                                                            }));
                                                            return;
                                                        }
                                                        if (!revealedBsns[key]) {
                                                            await fetchApplicationChildBsn(drawerApp.id, childIndex, key);
                                                        }
                                                    }}
                                                    aria-pressed={shown}
                                                    disabled={loading}
                                                    className="font-semibold text-indigo-600 underline underline-offset-2 transition hover:text-indigo-800 disabled:cursor-not-allowed disabled:opacity-60 dark:text-indigo-300 dark:hover:text-indigo-100"
                                                >
                                                    {shown
                                                        ? t("children.details.hide_bsn", { defaultValue: "Hide BSN" })
                                                        : t("children.details.show_bsn", { defaultValue: "Show BSN" })}
                                                </button>
                                            )}
                                            {error && (
                                                <span className="block basis-full text-xs font-semibold text-red-600 dark:text-red-300">
                                                    {error?.message || error?.title || String(error)}
                                                </span>
                                            )}
                                        </span>
                                    </>
                                );
                            };

                            return (
                                <>
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <h2
                                                id="application-drawer-title"
                                                className="text-xl font-bold text-gray-900 dark:text-gray-100"
                                            >
                                                {t("applications.drawer_title", { defaultValue: "Application details" })}
                                            </h2>
                                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                                {t("applications.request", "Aanvraag")} #{drawerApp.id ?? drawerEntry.index + 1}
                                            </p>
                                        </div>
                                        <button
                                            ref={drawerCloseRef}
                                            type="button"
                                            onClick={closeDrawer}
                                            aria-label={t("common.close", { defaultValue: "Close" })}
                                            className="rounded-full border border-gray-200 bg-white p-2 text-gray-600 shadow-sm transition hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>

                                    <div className="mt-6 flex flex-col gap-4">
                                        <section className="order-1 grid gap-3 rounded-2xl border border-gray-200 bg-white/85 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/50 sm:grid-cols-3">
                                            <div className="min-w-0">
                                                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    STATUS
                                                </div>
                                                <span className={`mt-1 inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClass}`}>
                                                    {statusLabel}
                                                </span>
                                            </div>

                                            <div className="min-w-0">
                                                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    BEHANDELAAR
                                                </div>
                                                <div className="mt-1 flex min-w-0 items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                                    <FaUserTie className="shrink-0 text-indigo-500 dark:text-indigo-300" />
                                                    <span className="truncate">
                                                        {assignedTeacherLabel ||
                                                            t("applications.no_handler_assigned", {
                                                                defaultValue: "No teacher assigned",
                                                            })}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="min-w-0">
                                                <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    ACTIES
                                                </div>
                                                <div className="mt-1 flex items-center gap-2">
                                                    {onboardingComplete && onApprove && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const eligibleChildren = getClassroomPlacementCandidates(children);
                                                                if (eligibleChildren.length) setPlacementDialog({ application: drawerApp, children: eligibleChildren });
                                                                else onApprove(drawerApp.id, childPlacements[drawerApp.id] || {});
                                                            }}
                                                            aria-label="Aanvraag goedkeuren"
                                                            title="Aanvraag goedkeuren"
                                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-green-200 bg-green-50 text-green-700 transition hover:bg-green-100 hover:text-green-900 focus:outline-none focus:ring-2 focus:ring-green-400/60 dark:border-green-500/50 dark:bg-green-900/40 dark:text-green-200"
                                                        >
                                                            <FaCheck />
                                                        </button>
                                                    )}
                                                    {onReject && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onReject(drawerApp.id)}
                                                            aria-label="Aanvraag afwijzen"
                                                            title="Aanvraag afwijzen"
                                                            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-700 transition hover:bg-red-100 hover:text-red-900 focus:outline-none focus:ring-2 focus:ring-red-400/60 dark:border-red-500/50 dark:bg-red-900/40 dark:text-red-200"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </section>

                                        <section className="order-2 overflow-hidden rounded-2xl border border-indigo-200 bg-indigo-50/50 shadow-sm transition-all duration-200 dark:border-indigo-500/40 dark:bg-indigo-950/20">
                                            <div className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <span className="min-w-0 flex-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                        {t("applications.workflow.title", { defaultValue: "Biography onboarding" })}
                                                    </span>
                                                    {onboardingComplete && <span className="text-xs font-semibold text-green-700 dark:text-green-300">{t("applications.workflow.completed", { defaultValue: "Completed" })}</span>}
                                                    <button
                                                        type="button"
                                                        onClick={() => setOnboardingExpanded((expanded) => !expanded)}
                                                        className="rounded-full p-1 text-indigo-700 transition hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:text-indigo-200 dark:hover:bg-indigo-900/50"
                                                        aria-expanded={onboardingExpanded}
                                                        aria-label={onboardingExpanded
                                                            ? t("applications.workflow.collapse", { defaultValue: "Collapse biography onboarding" })
                                                            : t("applications.workflow.expand", { defaultValue: "Expand biography onboarding" })}
                                                    >
                                                        {onboardingExpanded ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
                                                    </button>
                                                </div>
                                                <ol className="mt-4 grid grid-cols-5 gap-1" aria-label={t("applications.workflow.progress", { defaultValue: "Onboarding progress" })}>
                                                    {workflowSteps.map((step, index) => {
                                                        const accessible = step.completed || index === activeWorkflowStepIndex;
                                                        const selected = index === selectedWorkflowStepIndex && onboardingExpanded;
                                                        return (
                                                            <li key={step.key} className="flex min-w-0 items-start">
                                                                <button
                                                                    type="button"
                                                                    disabled={!accessible}
                                                                    onClick={() => {
                                                                        setEditingOnboardingStep(step.key);
                                                                        setOnboardingExpanded(true);
                                                                    }}
                                                                    aria-current={index === activeWorkflowStepIndex ? "step" : undefined}
                                                                    className="flex min-w-0 flex-1 flex-col items-center text-center disabled:cursor-default"
                                                                >
                                                                    <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-all duration-200 ${step.completed ? "border-green-600 bg-green-600 text-white" : index === activeWorkflowStepIndex ? "border-indigo-500 bg-white text-indigo-700 ring-4 ring-indigo-100 dark:bg-slate-900 dark:text-indigo-200 dark:ring-indigo-900/50" : "border-slate-300 bg-white text-slate-400 dark:border-slate-700 dark:bg-slate-900"} ${selected ? "shadow-md" : ""}`}>
                                                                        {step.completed ? <FaCheck aria-hidden="true" /> : index + 1}
                                                                    </span>
                                                                    <span title={step.shortLabel} className={`mt-1 w-full truncate text-[11px] font-semibold sm:text-xs ${step.completed || index === activeWorkflowStepIndex ? "text-gray-800 dark:text-gray-100" : "text-slate-400"}`}>{step.shortLabel}</span>
                                                                </button>
                                                                <span className={`mt-4 h-0.5 min-w-2 flex-1 ${(workflowSteps[index + 1] || approvedStep).completed ? "bg-green-500" : "bg-slate-300 dark:bg-slate-700"}`} />
                                                            </li>
                                                        );
                                                    })}
                                                    <li className="flex min-w-0 items-start">
                                                        <div
                                                            className="flex min-w-0 flex-1 flex-col items-center text-center"
                                                            aria-current={onboardingComplete && !approvedStep.completed ? "step" : undefined}
                                                            title={onboardingComplete && !approvedStep.completed
                                                                ? t("applications.workflow.approve_hint", { defaultValue: "Approve the application through Actions." })
                                                                : approvedStep.label}
                                                        >
                                                            <span className={`flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-bold transition-all duration-200 ${approvedStep.completed ? "border-green-600 bg-green-600 text-white" : onboardingComplete ? "border-indigo-500 bg-white text-indigo-700 ring-4 ring-indigo-100 dark:bg-slate-900 dark:text-indigo-200 dark:ring-indigo-900/50" : "border-slate-300 bg-white text-slate-400 dark:border-slate-700 dark:bg-slate-900"}`}>
                                                                {approvedStep.completed ? <FaCheck aria-hidden="true" /> : workflowSteps.length + 1}
                                                            </span>
                                                            <span className={`mt-1 w-full truncate text-[11px] font-semibold sm:text-xs ${approvedStep.completed || onboardingComplete ? "text-gray-800 dark:text-gray-100" : "text-slate-400"}`}>{approvedStep.label}</span>
                                                        </div>
                                                    </li>
                                                </ol>
                                                {onboardingComplete && !approvedStep.completed && (
                                                    <p className="mt-3 text-xs font-medium text-indigo-700 dark:text-indigo-200">
                                                        {t("applications.workflow.approve_hint", { defaultValue: "Approve the application through Actions." })}
                                                    </p>
                                                )}
                                            </div>

                                            {onboardingExpanded && (
                                                <div className="border-t border-indigo-200 p-4 dark:border-indigo-500/30">
                                                    <div key={selectedWorkflowStep.key} className="onboarding-panel-enter rounded-xl border border-indigo-200 bg-white p-4 dark:border-indigo-700 dark:bg-slate-900/80">
                                                                    <div className="flex items-start justify-between gap-3">
                                                                        <div>
                                                                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selectedWorkflowStep.label}</p>
                                                                            {selectedWorkflowStep.detail && <p className="mt-1 text-xs font-medium text-green-700 dark:text-green-300">{selectedWorkflowStep.detail}</p>}
                                                                            {selectedWorkflowStep.help && <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{selectedWorkflowStep.help}</p>}
                                                                        </div>
                                                                    </div>
                                                                    {selectedWorkflowStep.key === "HANDLER" && onAssignTeacher && (
                                                                        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                                                        <select
                                                                            value={drawerApp.assignedTeacherId ? String(drawerApp.assignedTeacherId) : ""}
                                                                            onChange={(event) => {
                                                                                onAssignTeacher(drawerApp.id, event.target.value || null);
                                                                                if (event.target.value) setEditingOnboardingStep(null);
                                                                            }}
                                                                            disabled={teachersLoading}
                                                                            aria-label={t("applications.workflow.choose_handler", { defaultValue: "Choose a teacher" })}
                                                                            className="min-w-0 flex-1 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-700 dark:bg-slate-950 dark:text-white"
                                                                        >
                                                                            <option value="">{teachersLoading ? t("applications.loading_handlers", { defaultValue: "Loading teachers..." }) : t("applications.no_handler_assigned", { defaultValue: "No teacher assigned" })}</option>
                                                                            {(Array.isArray(teachers) ? teachers : []).map((teacher) => {
                                                                                const key = teacher.id ?? teacher.teacherId ?? teacher.teacher_id ?? teacher.uuid ?? teacher.teacherUUID ?? teacher.teacherUuid;
                                                                                if (!key) return null;
                                                                                const label = teacherLookup.get?.(String(key)) || [teacher.firstName, teacher.lastName].filter(Boolean).join(" ") || [teacher.first_name, teacher.last_name].filter(Boolean).join(" ") || teacher.displayName || String(key);
                                                                                return <option key={key} value={String(key)}>{label}</option>;
                                                                            })}
                                                                        </select>
                                                                        <button
                                                                            type="button"
                                                                            disabled={teachersLoading || !currentTeacherId || String(drawerApp.assignedTeacherId || "") === String(currentTeacherId)}
                                                                            title={!currentTeacherId ? t("applications.workflow.no_linked_teacher", { defaultValue: "No teacher profile is linked to your account" }) : undefined}
                                                                            onClick={async () => {
                                                                                if (!currentTeacherId) return;
                                                                                await onAssignTeacher(drawerApp.id, String(currentTeacherId));
                                                                                setEditingOnboardingStep(null);
                                                                            }}
                                                                            className={`shrink-0 rounded-lg border px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 ${!currentTeacherId || String(drawerApp.assignedTeacherId || "") === String(currentTeacherId)
                                                                                ? "cursor-not-allowed border-slate-300 bg-slate-100 text-slate-400 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500"
                                                                                : "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 focus:ring-indigo-300 dark:border-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-200 dark:hover:bg-indigo-900/60"
                                                                            }`}
                                                                        >
                                                                            {String(drawerApp.assignedTeacherId || "") === String(currentTeacherId) && currentTeacherId
                                                                                ? t("applications.workflow.assigned_to_me", { defaultValue: "Assigned to me" })
                                                                                : t("applications.workflow.assign_to_me", { defaultValue: "Assign to me" })}
                                                                        </button>
                                                                        </div>
                                                                    )}
                                                                    {selectedWorkflowStep.requiresDate && (
                                                                        <input
                                                                            type="datetime-local"
                                                                            defaultValue={toAmsterdamDateTimeInputValue(drawerApp[selectedWorkflowStep.field])}
                                                                            disabled={!onWorkflowStep}
                                                                            aria-label={selectedWorkflowStep.label}
                                                                            onChange={async (event) => {
                                                                                const scheduledAt = event.target.value;
                                                                                if (scheduledAt) event.currentTarget.blur();
                                                                                if (scheduledAt) {
                                                                                    setEditingOnboardingStep(null);
                                                                                    setOnboardingExpanded(false);
                                                                                }
                                                                                const updated = await onWorkflowStep(drawerApp.id, selectedWorkflowStep.key, Boolean(scheduledAt), scheduledAt ? { scheduledAt } : {});
                                                                                if (scheduledAt && updated === false) {
                                                                                    setEditingOnboardingStep(selectedWorkflowStep.key);
                                                                                    setOnboardingExpanded(true);
                                                                                }
                                                                            }}
                                                                            className="mt-3 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-indigo-700 dark:bg-slate-950 dark:text-white"
                                                                        />
                                                                    )}
                                                                    {!selectedWorkflowStep.requiresDate && selectedWorkflowStep.key !== "HANDLER" && (() => {
                                                                        const undoBlockReason = selectedWorkflowStep.completed
                                                                            ? getUndoBlockReason(drawerApp, selectedWorkflowStep.key)
                                                                            : null;
                                                                        return (
                                                                        <>
                                                                        <button
                                                                            type="button"
                                                                            disabled={!onWorkflowStep || Boolean(undoBlockReason)}
                                                                            onClick={async () => {
                                                                                const completingOnboarding = selectedWorkflowStep.key === "BIOGRAPHY_MEETING"
                                                                                    && !selectedWorkflowStep.completed;
                                                                                const updated = await onWorkflowStep(drawerApp.id, selectedWorkflowStep.key, !selectedWorkflowStep.completed);
                                                                                if (updated === false) return;
                                                                                setEditingOnboardingStep(null);
                                                                                if (completingOnboarding) {
                                                                                    const eligibleChildren = getClassroomPlacementCandidates(children);
                                                                                    if (eligibleChildren.length) {
                                                                                        setPlacementDialog({ application: drawerApp, children: eligibleChildren });
                                                                                    }
                                                                                }
                                                                            }}
                                                                            className="mt-3 rounded-full bg-indigo-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                                                        >
                                                                            {selectedWorkflowStep.completed
                                                                                ? t("applications.workflow.undo", { defaultValue: "Undo" })
                                                                                : selectedWorkflowStep.confirmLabel || t("applications.workflow.confirm", { defaultValue: "Confirm" })}
                                                                        </button>
                                                                        {undoBlockReason && (
                                                                            <p className="mt-2 text-xs font-medium text-amber-700 dark:text-amber-300">
                                                                                {undoBlockMessages[undoBlockReason]}
                                                                            </p>
                                                                        )}
                                                                        </>
                                                                        );
                                                                    })()}
                                                        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-slate-800">
                                                            <button
                                                                type="button"
                                                                disabled={selectedWorkflowStepIndex === 0}
                                                                onClick={() => setEditingOnboardingStep(workflowSteps[selectedWorkflowStepIndex - 1].key)}
                                                                className="text-xs font-semibold text-indigo-700 hover:underline disabled:invisible dark:text-indigo-300"
                                                            >
                                                                {t("applications.workflow.previous", { defaultValue: "Previous" })}
                                                            </button>
                                                            <span className="text-xs text-slate-500 dark:text-slate-400">
                                                                {t("applications.workflow.step_count", {
                                                                    current: selectedWorkflowStepIndex + 1,
                                                                    // The approval step counts too, so the numbering matches the stepper above and the list.
                                                                    total: workflowSteps.length + 1,
                                                                    defaultValue: "Step {{current}} of {{total}}",
                                                                })}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                disabled={
                                                                    selectedWorkflowStepIndex >= workflowSteps.length - 1 ||
                                                                    !(
                                                                        workflowSteps[selectedWorkflowStepIndex + 1]?.completed ||
                                                                        selectedWorkflowStepIndex + 1 === activeWorkflowStepIndex
                                                                    )
                                                                }
                                                                onClick={() => setEditingOnboardingStep(workflowSteps[selectedWorkflowStepIndex + 1].key)}
                                                                className="text-xs font-semibold text-indigo-700 hover:underline disabled:invisible dark:text-indigo-300"
                                                            >
                                                                {t("applications.workflow.next", { defaultValue: "Next" })}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </section>

                                        <section className="order-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-md shadow-indigo-100/40 dark:border-gray-700 dark:bg-gray-900/70 dark:shadow-indigo-950/20">
                                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                                {t("applications.parent", { defaultValue: "Parent" })}
                                            </h3>
                                            <div className="mt-3 space-y-4">
                                                {parents.map((parent, idx) => (
                                                    <div key={idx} className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                                                        <p className="font-semibold text-gray-900 dark:text-gray-100">
                                                            <FaUser className="mr-2 inline text-gray-600 dark:text-gray-300" />
                                                            {personName(parent)}
                                                        </p>
                                                        <p>
                                                            <FaEnvelope className="mr-2 inline text-gray-600 dark:text-gray-300" />
                                                            {parent.email || "—"}
                                                        </p>
                                                        <p>
                                                            <FaPhone className="mr-2 inline text-gray-600 dark:text-gray-300" />
                                                            {parent.phone || "—"}
                                                        </p>
                                                        <p>
                                                            <FaHome className="mr-2 inline text-gray-600 dark:text-gray-300" />
                                                            {formatAddress(parent)}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </section>

                                        <section className="order-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-md shadow-indigo-100/40 dark:border-gray-700 dark:bg-gray-900/70 dark:shadow-indigo-950/20">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                                                    {t("applications.children", { defaultValue: "Child(ren)" })}
                                                </h3>
                                                <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
                                                    {children.length}
                                                </span>
                                            </div>
                                            <div className="mt-3 space-y-2">
                                                {children.map((child, idx) => {
                                                    const childKey = `${drawerApp.id ?? "app"}:${idx}`;
                                                    const age = getChildAge(child.dateBirth || child.dateOfBirth || child.birthDate);
                                                    const placement = childPlacements[drawerApp.id]?.[idx];
                                                    return (
                                                        <div
                                                            key={childKey}
                                                            className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950/60"
                                                        >
                                                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 sm:flex-nowrap">
                                                                {renderChildRow(child, idx)}
                                                            </div>
                                                            {age !== null && age >= 7 && onApprove && child.currentClassroomId && (
                                                                <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-300">
                                                                    {t("applications.placement.already_placed", {
                                                                        defaultValue: "Already in Lumen and placed in {{className}}; no placement needed.",
                                                                        className: child.currentClassName || t("applications.placement.unknown_classroom", { defaultValue: "Unknown classroom" }),
                                                                    })}
                                                                </div>
                                                            )}
                                                            {age !== null && age >= 7 && onApprove && !child.currentClassroomId && (
                                                                <div className="border-t border-indigo-100 bg-indigo-50/60 px-4 py-3 dark:border-indigo-900/60 dark:bg-indigo-950/30">
                                                                    <label className="block text-xs font-semibold text-indigo-900 dark:text-indigo-100">
                                                                        {t("applications.placement.label", { defaultValue: "Place directly in a classroom" })}
                                                                        <select
                                                                            value={placement?.classroomId || ""}
                                                                            disabled={classroomsLoading}
                                                                            onChange={(event) => setPlacement(drawerApp.id, idx, event.target.value)}
                                                                            className="mt-2 h-10 w-full rounded-xl border border-indigo-200 bg-white px-3 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-indigo-700 dark:bg-slate-950 dark:text-white"
                                                                        >
                                                                            <option value="">{classroomsLoading ? t("common.loading", { defaultValue: "Loading..." }) : t("applications.placement.no_selection", { defaultValue: "No classroom selected" })}</option>
                                                                            {classrooms.map((classroom) => <option key={classroom.id} value={classroom.id}>{classroomLabel(classroom)}</option>)}
                                                                        </select>
                                                                    </label>
                                                                    <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">
                                                                        {t("applications.placement.help", { defaultValue: "The child will be assigned as current when the application is approved." })}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </section>

                                        {(onDelete || onRestore) && (
                                            <section className="order-4 rounded-2xl border border-gray-200 bg-white/70 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/40">
                                                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                                                    {onDelete && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onDelete(drawerApp.id)}
                                                            className="inline-flex items-center justify-center gap-2 rounded-full border border-red-100/70 bg-red-50/80 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-100 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-200"
                                                        >
                                                            <FaTrash />
                                                            {t("common.delete", "Verwijder")}
                                                        </button>
                                                    )}
                                                    {onRestore && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onRestore(drawerApp.id)}
                                                            className="inline-flex items-center justify-center gap-2 rounded-full border border-sky-200/70 bg-sky-50/80 px-3 py-1.5 text-xs font-semibold text-sky-700 transition hover:bg-sky-100 dark:border-sky-500/40 dark:bg-sky-900/30 dark:text-sky-200"
                                                        >
                                                            <FaUndo />
                                                            {t("common.restore", "Herstel")}
                                                        </button>
                                                    )}
                                                </div>
                                            </section>
                                        )}

                                        <p className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                            <FaLock className="shrink-0" />
                                            {t("applications.privacy_hint", {
                                                defaultValue: "Alle gegevens worden veilig en vertrouwelijk verwerkt.",
                                            })}
                                        </p>
                                    </div>
                                </>
                            );
                        })()}
                    </aside>
                </div>
            ), document.body)}
            {placementDialog && typeof document !== "undefined" && createPortal((
                <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-sm">
                    <div role="dialog" aria-modal="true" aria-labelledby="application-placement-title" className="w-full max-w-xl rounded-3xl border border-indigo-100 bg-white p-6 shadow-2xl dark:border-indigo-800 dark:bg-slate-950">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-300">{t("applications.placement.step", { defaultValue: "Classroom placement" })}</p>
                                <h2 id="application-placement-title" className="mt-1 text-xl font-bold text-slate-950 dark:text-white">{t("applications.placement.title", { defaultValue: "Place children aged 7 and older" })}</h2>
                                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{t("applications.placement.description", { defaultValue: "Choose a current classroom now, or continue without placement." })}</p>
                            </div>
                            <button type="button" onClick={() => setPlacementDialog(null)} aria-label={t("common.close", { defaultValue: "Close" })} className="rounded-full border border-slate-200 p-2 text-slate-500 dark:border-slate-700 dark:text-slate-300"><FaTimes /></button>
                        </div>
                        <div className="mt-5 space-y-4">
                            {placementDialog.children.map(({ child, index, age }) => (
                                <label key={`${placementDialog.application.id}:${index}`} className="block rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm font-semibold text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                                    {child.firstName} {child.lastName} · {age} {t("applications.placement.years", { defaultValue: "years" })}
                                    <select value={childPlacements[placementDialog.application.id]?.[index]?.classroomId || ""} disabled={classroomsLoading} onChange={(event) => setPlacement(placementDialog.application.id, index, event.target.value)} className="mt-2 h-11 w-full rounded-xl border border-indigo-200 bg-white px-3 text-sm text-slate-900 dark:border-indigo-700 dark:bg-slate-950 dark:text-white">
                                        <option value="">{classroomsLoading ? t("common.loading", { defaultValue: "Loading..." }) : t("applications.placement.no_selection", { defaultValue: "No classroom selected" })}</option>
                                        {classrooms.map((classroom) => <option key={classroom.id} value={classroom.id}>{classroomLabel(classroom)}</option>)}
                                    </select>
                                </label>
                            ))}
                        </div>
                        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button type="button" onClick={() => setPlacementDialog(null)} className="rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 dark:border-slate-600 dark:text-slate-200">{t("common.cancel", { defaultValue: "Cancel" })}</button>
                            <button type="button" onClick={() => { const applicationId = placementDialog.application.id; setPlacementDialog(null); onApprove(applicationId, childPlacements[applicationId] || {}); }} className="rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700">{t("applications.placement.continue", { defaultValue: "Continue approval" })}</button>
                        </div>
                    </div>
                </div>
            ), document.body)}
        </div>
    );
};

export default FamilyApplicationTable;
